// Camel movement and board queries - the mechanical heart of the game,
// shared by the dispatch path, the leg-outcome enumerator, and the bots.
// Everything cites docs/rules.pdf.
//
// The track is an array of stacks (bottom -> top), index 0 = flag space 1.
// It extends past TRACK_LENGTH because the finishing move completes: a die
// can carry a whole stack up to 3 past the line (+1 more for an oasis).

import { TRACK_LENGTH, CAMELS } from './constants';

export const emptyTrack = () => Array.from({ length: TRACK_LENGTH + 5 }, () => []);

export const spaceOfCamel = (track, camel) => track.findIndex((stack) => stack.includes(camel));

/**
 * Ranking, best first: the camel farthest along wins, and within a stack
 * "a Camel that sits on top of another Camel is always considered ahead"
 * (p2, Important).
 */
export const rankCamels = (track) => {
  const ranked = [];
  for (let space = track.length - 1; space >= 0; space -= 1) {
    for (let height = track[space].length - 1; height >= 0; height -= 1) {
      ranked.push(track[space][height]);
    }
  }
  return ranked;
};

/**
 * Moves a camel unit: the camel plus every camel ON TOP of it (p2), landing
 * on top of any stack it reaches (p3). Desert tiles: the unit bounces one
 * space (+1 oasis, -1 mirage), the tile's owner is owed 1 EP, and a mirage
 * bounce lands the unit UNDERNEATH any stack on the target space (p3).
 * Tiles are never adjacent (placement rule, p3), so a bounce can never hit
 * a second tile - no chain to resolve.
 *
 * Pure on its inputs: returns { track, desertSeat } - desertSeat is the
 * owner owed a pound, or null. `desert` is { seat: { space, side } }.
 */
export const moveCamel = (track, camel, distance, desert = {}) => {
  const next = track.map((stack) => stack.slice());
  const from = spaceOfCamel(next, camel);
  const stack = next[from];
  const height = stack.indexOf(camel);
  const unit = stack.splice(height); // the camel and everything above it

  let to = Math.min(from + distance, next.length - 1);
  let desertSeat = null;
  let under = false;

  const tileAt = (space) => Object.entries(desert)
    .find(([, t]) => t && t.space === space);
  const hit = tileAt(to);
  if (hit) {
    const [seat, tile] = hit;
    desertSeat = Number(seat);
    if (tile.side === 'oasis') {
      to = Math.min(to + 1, next.length - 1);
    } else {
      to = Math.max(0, to - 1);
      under = true;
    }
  }

  if (under) next[to].unshift(...unit);
  else next[to].push(...unit);
  return { track: next, desertSeat };
};

export const raceWon = (track) => track
  .slice(TRACK_LENGTH)
  .some((stack) => stack.length > 0);

/**
 * Where a desert tile may go (p3): an empty space - no camels, no desert
 * tile - not adjacent to another desert tile, and never space 1. (Indexes
 * here are 0-based; "space 1" is index 0.) Your own tile's current spot
 * doesn't block its own move.
 */
export const legalDesertSpaces = (track, desert, movingSeat = null) => {
  const taken = new Set();
  Object.entries(desert).forEach(([seat, tile]) => {
    if (!tile || Number(seat) === movingSeat) return;
    taken.add(tile.space);
    taken.add(tile.space - 1);
    taken.add(tile.space + 1);
  });
  const spaces = [];
  for (let space = 1; space < TRACK_LENGTH; space += 1) {
    if (track[space].length === 0 && !taken.has(space)) spaces.push(space);
  }
  return spaces;
};

export const camelIndex = (camel) => CAMELS.indexOf(camel);
